/**
 * Browser-side OAuth 2.1 + PKCE + Dynamic Client Registration helpers
 * for MCP servers (per the MCP authorization spec).
 *
 * Designed to be transport-agnostic. Callers stash arbitrary `ctx`
 * (tenantId + serverId, typically) when starting the dance; we hand
 * it back on the exchange-code side so the callback page can route
 * the resulting tokens to the right connection record.
 *
 * Public client only — `token_endpoint_auth_method: "none"`. Tokens
 * land in localStorage via registry.js; the verifier lives in
 * sessionStorage so a tab-close mid-flow doesn't leak a partial
 * challenge.
 */

const PKCE_STATE_KEY = "mcp:pkce";

function bufToBase64Url(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomBase64Url(byteLen = 32) {
  const bytes = new Uint8Array(byteLen);
  crypto.getRandomValues(bytes);
  return bufToBase64Url(bytes.buffer);
}

async function pkceChallenge(verifier) {
  const enc = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", enc);
  return bufToBase64Url(hash);
}

// Fetch the RFC 8414 authorization-server metadata document. MCP
// servers advertise the issuer == server origin; the metadata sits at
// `${issuer}/.well-known/oauth-authorization-server`.
export async function fetchAuthMetadata(issuer, { signal } = {}) {
  const url = `${issuer.replace(/\/+$/, "")}/.well-known/oauth-authorization-server`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`OAuth metadata ${res.status}`);
  return res.json();
}

// Dynamic client registration (RFC 7591). Anonymous public client —
// no client_secret is issued because we request `token_endpoint_auth_method: "none"`.
export async function registerClient(metadata, { redirectUri, clientName, signal }) {
  const res = await fetch(metadata.registration_endpoint, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_name: clientName,
      redirect_uris: [redirectUri],
      grant_types: ["authorization_code", "refresh_token"],
      response_types: ["code"],
      token_endpoint_auth_method: "none",
      application_type: "web",
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Dynamic client registration ${res.status}: ${text}`);
  }
  return res.json();
}

// Drop the user at the authorization endpoint. Stashes the verifier
// + everything needed to finish the dance into sessionStorage so
// the callback page can complete without consulting any other state.
export async function startAuthorize({ metadata, clientId, redirectUri, scope, ctx }) {
  const verifier = randomBase64Url(32);
  const challenge = await pkceChallenge(verifier);
  const state = randomBase64Url(16);
  const stash = { verifier, state, metadata, clientId, redirectUri, ctx };
  sessionStorage.setItem(PKCE_STATE_KEY, JSON.stringify(stash));
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
  });
  if (scope) params.set("scope", scope);
  const url = `${metadata.authorization_endpoint}?${params}`;
  window.location.assign(url);
}

export function readPendingAuthorize() {
  try {
    const raw = sessionStorage.getItem(PKCE_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingAuthorize() {
  try {
    sessionStorage.removeItem(PKCE_STATE_KEY);
  } catch {}
}

// Exchange the authorization code returned in the callback for an
// access + refresh token pair. Returns `{ tokens, ctx }` so the
// caller can route the result back to the originating server.
export async function exchangeCode({ code, state }) {
  const stash = readPendingAuthorize();
  if (!stash) throw new Error("No pending OAuth state — session expired or never started.");
  if (state !== stash.state) throw new Error("OAuth state mismatch (possible CSRF).");
  const params = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    code_verifier: stash.verifier,
    redirect_uri: stash.redirectUri,
    client_id: stash.clientId,
  });
  const res = await fetch(stash.metadata.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params,
  });
  clearPendingAuthorize();
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Token exchange ${res.status}: ${text}`);
  }
  return { tokens: await res.json(), ctx: stash.ctx };
}

export async function refreshTokens({ refreshToken, metadata, clientId }) {
  const params = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: clientId,
  });
  const res = await fetch(metadata.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Refresh ${res.status}: ${text}`);
  }
  return res.json();
}
